import { bookshelfGroups } from "@shared/bookshelf";
import { Plus } from "lucide-react";
import type { ReactNode } from "react";
import { ScrollArea } from "@web/components/ui/scroll-area";
import type { BookshelfViewItem, ChapterRef } from "../../../api";
import type { BookDetailPanel, BookSourceItem, DisplaySourceOption } from "../../../shelf/types";

export function BookDetailPickerPanel({
  panel,
  catalogChapters,
  catalogError,
  catalogLoading,
  groupId,
  item,
  sourceOptions,
  selectedSource,
  onChapterOpen,
  onClose,
  onGroupChange,
  onSourceChange,
}: {
  panel: Exclude<BookDetailPanel, null>;
  catalogChapters: ChapterRef[];
  catalogError?: string | null;
  catalogLoading?: boolean;
  groupId: string;
  item: BookshelfViewItem;
  sourceOptions: DisplaySourceOption[];
  selectedSource?: BookSourceItem;
  onChapterOpen: (chapterId: string) => void;
  onClose: () => void;
  onGroupChange: (groupId: string) => void;
  onSourceChange: (sourceId: string) => void;
}) {
  const titleByPanel = {
    source: "切换书源",
    group: "设置分组",
    catalog: "查看目录",
  };
  const metaByPanel = {
    source: `${sourceOptions.length} 个书源`,
    group: `${bookshelfGroups.length} 个分组`,
    catalog: `${catalogChapters.length} 章`,
  };

  return (
    <BookDetailOverlayPanel title={titleByPanel[panel]} action={renderPanelAction()} meta={metaByPanel[panel]} onClose={onClose}>
      {panel === "source" && (
        <SourcePicker
          options={sourceOptions}
          selectedSource={selectedSource}
          onSourceChange={onSourceChange}
        />
      )}
      {panel === "group" && <GroupPicker groupId={groupId} onGroupChange={onGroupChange} />}
      {panel === "catalog" && (
        <CatalogPicker
          chapters={catalogChapters}
          error={catalogError}
          loading={catalogLoading}
          progress={item.progress}
          onChapterOpen={onChapterOpen}
        />
      )}
    </BookDetailOverlayPanel>
  );

  function renderPanelAction() {
    if (panel === "group") {
      return (
        <IconActionButton ariaLabel="添加分组">
          <Plus className="size-3.5" />
        </IconActionButton>
      );
    }

    return null;
  }
}

function SourcePicker({
  options,
  selectedSource,
  onSourceChange,
}: {
  options: DisplaySourceOption[];
  selectedSource?: BookSourceItem;
  onSourceChange: (sourceId: string) => void;
}) {
  return (
    <div className="grid gap-2">
      {options.map(source => (
        <BookChoiceButton
          key={source.id}
          active={source.sourceId === selectedSource?.sourceId && source.sourceBookId === selectedSource?.sourceBookId && source.real}
          title={source.name}
          description={source.latestChapterTitle}
          onClick={() => onSourceChange(source.sourceId)}
        />
      ))}
    </div>
  );
}

function GroupPicker({ groupId, onGroupChange }: { groupId: string; onGroupChange: (groupId: string) => void }) {
  return (
    <div className="grid content-start gap-2">
      {bookshelfGroups.map(group => (
        <BookChoiceButton key={group.id} active={group.id === groupId} title={group.label} onClick={() => onGroupChange(group.id)} />
      ))}
      <BookChoiceButton active={false} title="新建分组" description="点击右上角 + 添加自定义分组" onClick={() => undefined} />
    </div>
  );
}

function CatalogPicker({
  chapters,
  error,
  loading,
  progress,
  onChapterOpen,
}: {
  chapters: ChapterRef[];
  error?: string | null;
  loading?: boolean;
  progress: BookshelfViewItem["progress"];
  onChapterOpen: (chapterId: string) => void;
}) {
  return (
    <div>
      {chapters.map(chapter => {
        const activeChapter = chapter.id === progress?.chapterId || chapter.index === progress?.chapterIndex;

        return (
          <button
            key={chapter.id}
            type="button"
            className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-xs transition-colors focus-visible:outline-none ${
              activeChapter ? "bg-foreground text-background" : "hover:bg-accent focus-visible:bg-accent"
            }`}
            onClick={() => onChapterOpen(chapter.id)}
          >
            <span className={`w-10 shrink-0 text-[11px] ${activeChapter ? "text-background/70" : "text-muted-foreground"}`}>
              {chapter.index + 1}
            </span>
            <span className="truncate">{chapter.title}</span>
          </button>
        );
      })}
      {chapters.length === 0 && (
        <p className="px-3 py-8 text-center text-sm text-muted-foreground">
          {loading ? "正在加载目录..." : error ?? "暂无可展示目录"}
        </p>
      )}
    </div>
  );
}

function BookChoiceButton({
  active,
  title,
  description,
  onClick,
}: {
  active: boolean;
  title: string;
  description?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? "bg-foreground text-background" : "hover:bg-accent"}`}
      onClick={onClick}
    >
      <span className="block text-sm font-medium">{title}</span>
      {description && <span className={`mt-0.5 block truncate text-[11px] ${active ? "text-background/70" : "text-muted-foreground"}`}>{description}</span>}
    </button>
  );
}

function BookDetailOverlayPanel({
  title,
  action,
  meta,
  children,
  onClose,
}: {
  title: string;
  action: ReactNode;
  meta: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end bg-black/35 p-3 sm:items-center sm:justify-center"
      onClick={event => {
        event.stopPropagation();
        onClose();
      }}
    >
      {/* 二级选择面板覆盖详情卡 避免在原卡片里挤出新内容 */}
      <section
        className="max-h-[76vh] w-full max-w-md overflow-hidden rounded-2xl border bg-background shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b bg-muted/30 px-4 py-2.5">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{meta}</p>
          </div>
          {action}
        </div>
        <ScrollArea className="h-[calc(76vh-4.5rem)] p-3">{children}</ScrollArea>
      </section>
    </div>
  );
}

function IconActionButton({ ariaLabel, children, onClick }: { ariaLabel: string; children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      className="rounded-full border bg-background p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
